// Single "is this a real number?" check for the whole chart.
// Rejects null, undefined, "", "   ", booleans, objects, NaN, Infinity.
// Number(null) is 0, Number("") is 0, Number(true) is 1 — never use
// Number(x) alone to detect missing API values.
export const hasNum = (value) =>
    (typeof value === "number" ||
        (typeof value === "string" && value.trim() !== "")) &&
    Number.isFinite(Number(value));

// Fields the IV Range chart reads.
// If the API sends any of these as text ("12.88"), they are converted to numbers.
const NUMERIC_FIELDS = [
    "iv",
    "iv1",
    "rng",
    "rng1",
    "high_range",
    "low_range",
    "usd",
    "lsd",
    "usd1",
    "lsd1",
    "open",
    "high",
    "low",
    "close",
    "ce_iv",
    "pe_iv",
    "ce_iv1",
    "pe_iv1",
    "vix",
    "vix1",
];

const OHLC_FIELDS = ["open", "high", "low", "close"];
// Next series. They exist on realized minutes and change on each live poll.
// Projection minutes and the trailing live-minute duplicate leave them null.
const NEXT_SD_FIELDS = ["iv1", "usd1", "lsd1"];

function fieldHasChartNumber(key, value) {
    if (hasNum(value)) return true;
    return (
        key === "rng" &&
        value &&
        typeof value === "object" &&
        hasNum(value.parsedValue)
    );
}

/** "15:07" -> 907. Bad input -> MAX_SAFE_INTEGER (sorts last). Use this everywhere. */
export function rowMinutesFromTime(time) {
    if (time == null) return Number.MAX_SAFE_INTEGER;
    const [h, m] = String(time).split(":").map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) {
        return Number.MAX_SAFE_INTEGER;
    }
    return h * 60 + m;
}

/**
 * Normalize every numeric field consumed by the IV Range chart.
 * "12.88" -> 12.88, { parsedValue: 156 } (rng) -> 156, null stays null.
 */
export function normalizeSessionRows(candles) {
    if (!Array.isArray(candles)) return [];

    return candles
        .filter((row) => row && row.time)
        .map((row) => {
            const out = { ...row };

            NUMERIC_FIELDS.forEach((key) => {
                const value = row[key];

                if (hasNum(value)) {
                    out[key] = Number(value);
                    return;
                }

                if (
                    key === "rng" &&
                    value &&
                    typeof value === "object" &&
                    hasNum(value.parsedValue)
                ) {
                    out[key] = Number(value.parsedValue);
                }
            });

            return out;
        })
        .filter((row) => rowHasChartField(row));
}

/**
 * True only when ALL four OHLC values are real numbers.
 * This answers "can this row draw a candle?" — not "should the row exist?".
 * (.some() let a row with only `close` through, and the renderer then
 * called yScale(null) === yScale(0) and drew a broken candle.)
 */
export function rowHasOhlc(row) {
    if (!row) return false;
    return OHLC_FIELDS.every((key) => hasNum(row[key]));
}

/**
 * True when the row has at least one numeric chart field.
 * Incomplete OHLC does not make the row useless: IV / USD / LSD can still render.
 */
export function rowHasChartField(row) {
    if (!row) return false;
    return NUMERIC_FIELDS.some((key) => fieldHasChartNumber(key, row[key]));
}

/**
 * Drops every minute after the last full OHLC candle, including minutes that
 * still have IV, USD, or LSD. The chart must not call this. Row retention is
 * rowHasChartField(); candle rendering is rowHasOhlc().
 */
export function trimIvOnlyTailAfterLastOhlc(rows) {
    if (!Array.isArray(rows) || !rows.length) return rows ?? [];
    let lastOhlcIndex = -1;
    for (let i = 0; i < rows.length; i += 1) {
        if (rowHasOhlc(rows[i])) lastOhlcIndex = i;
    }
    if (lastOhlcIndex < 0) return rows;
    return rows.slice(0, lastOhlcIndex + 1);
}

/**
 * Merge one live/history candle into an existing row.
 * Incoming nulls never wipe values we already have.
 */
export function mergeCandleRow(existing, incoming) {
    if (!existing) return incoming;
    if (!incoming) return existing;
    const out = { ...existing };
    Object.entries(incoming).forEach(([key, value]) => {
        if (key === "time") {
            out.time = incoming.time ?? existing.time;
            return;
        }
        if (NUMERIC_FIELDS.includes(key)) {
            // A non-numeric incoming value never wipes a real number.
            if (
                !fieldHasChartNumber(key, value) &&
                fieldHasChartNumber(key, existing[key])
            ) {
                return;
            }
        } else if (value == null && existing[key] != null) {
            return;
        }
        out[key] = value;
    });
    return out;
}


/** Skip padded minutes that have no numeric chart field at all. */
export function filterMergeableCandleRows(rows) {
    if (!Array.isArray(rows)) return [];
    return rows.filter((row) => row?.time && rowHasChartField(row));
}

function rowHasNextSd(row) {
    if (!row) return false;
    return NEXT_SD_FIELDS.some((key) => fieldHasChartNumber(key, row[key]));
}

/**
 * Trailing copy of the live minute: full OHLC, sometimes a collapsed or
 * end-of-day iv/usd/lsd, and no Next SD. Copying its indicators onto the
 * real bar draws a spike (iv jumps to the projection cap). OHLC can still
 * refresh; iv / iv1 / usd / lsd / usd1 / lsd1 stay on the row that has them.
 */
function isOhlcOnlyLivePatch(existing, incoming) {
    if (!rowHasOhlc(incoming) || rowHasNextSd(incoming)) return false;
    return rowHasChartField(existing);
}

function mergeOhlcOnly(existing, incoming) {
    const out = { ...existing };
    OHLC_FIELDS.forEach((key) => {
        if (hasNum(incoming[key])) out[key] = Number(incoming[key]);
    });
    return out;
}

/** Later duplicate of the same minute inside ONE array.
 *  A live candle that carries Next SD updates iv1 / usd1 / lsd1.
 *  A trailing patch with no Next SD only refreshes OHLC. */
export function mergeDuplicateTimeRow(first, later) {
    if (!first) return later;
    if (!later) return first;
    if (isOhlcOnlyLivePatch(first, later)) return mergeOhlcOnly(first, later);
    return mergeCandleRow(first, later);
}

export function collapseDuplicateTimeRows(rows) {
    const byTime = new Map();
    for (const row of rows || []) {
        if (!row?.time) continue;
        byTime.set(
            row.time,
            byTime.has(row.time)
                ? mergeDuplicateTimeRow(byTime.get(row.time), row)
                : row,
        );
    }
    return [...byTime.values()];
}

/**
 * Same-minute merge for live polls.
 * Realized candles carry iv1 / usd1 / lsd1 and must overwrite the projection
 * row already stored at that minute (those fields are null on projections).
 * The trailing OHLC duplicate must not overwrite indicators.
 */
export function mergeRowsAtSameTime(existing, incoming) {
    if (!existing) return incoming;
    if (!incoming) return existing;
    if (isOhlcOnlyLivePatch(existing, incoming)) {
        return mergeOhlcOnly(existing, incoming);
    }
    return mergeCandleRow(existing, incoming);
}

/**
 * Dedupe by `time`, sort by minute, drop rows with no chart fields.
 * Keeps API projection minutes (IV / USD / LSD after the last candle).
 * `lastTime` is accepted for call-site compatibility but is not used to
 * trim projection rows — only all-null pads are removed via rowHasChartField.
 */
export function cleanRows(rows, _lastTime) {
    let out = collapseDuplicateTimeRows(rows).sort(
        (a, b) => rowMinutesFromTime(a.time) - rowMinutesFromTime(b.time),
    );
    return out.filter((r) => rowHasChartField(r));
}

/** One poll's session: dedupe duplicate minutes, drop empty pads, keep projections. */
export function preparePollSessionRows(session, lastTime) {
    return cleanRows(
        collapseDuplicateTimeRows(filterMergeableCandleRows(session)),
        lastTime,
    );
}

export function cleanApiPayloadSessions(payload) {
    if (!payload || !Array.isArray(payload.cc_data)) return payload;
    const liveDate = payload.liveDate;
    const lastTime = payload.lastTime;
    const dateList = payload.dateList || [];
    return {
        ...payload,
        cc_data: payload.cc_data.map((session, index) => {
            const cutoff =
                liveDate && dateList[index] === liveDate ? lastTime : undefined;
            return cleanRows(session, cutoff);
        }),
    };
}

/** API `lastTime` is minutes since midnight (907 -> "15:07"), not seconds. */
export function minutesSinceMidnightToTimeKey(totalMinutes) {
    if (!hasNum(totalMinutes)) return null; // Number(null) would give "00:00"
    const n = Number(totalMinutes);
    if (n < 0 || n >= 24 * 60) return null;
    const h = Math.floor(n / 60);
    const m = Math.floor(n % 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Which minute bar is forming: prefer lastDataTime ("15:07:28"), else lastTime. */
export function resolveLiveMinuteKey(lastDataTime, lastTime) {
    if (lastDataTime) {
        const parts = String(lastDataTime).split(":");
        if (parts.length >= 2) {
            return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
        }
    }
    return minutesSinceMidnightToTimeKey(lastTime);
}

/** Live price for the marker — not the last array slot when API pads future minutes. */
export function resolveLiveCloseFromSession(rows, lastDataTime, lastTime) {
    if (!Array.isArray(rows) || !rows.length) return null;
    const minuteKey = resolveLiveMinuteKey(lastDataTime, lastTime);
    if (minuteKey) {
        const match = rows.find((r) => r?.time === minuteKey);
        if (hasNum(match?.close)) return Number(match.close);
    }
    for (let i = rows.length - 1; i >= 0; i -= 1) {
        if (hasNum(rows[i]?.close)) return Number(rows[i].close);
    }
    return null;
}

export function sessionInTradingWindow(candles) {
    if (!Array.isArray(candles)) return [];
    const startMinutes = 9 * 60 + 15;
    const endMinutes = 15 * 60 + 30;

    return candles.filter((row) => {
        const mins = rowMinutesFromTime(row?.time);
        return mins >= startMinutes && mins <= endMinutes;
    });
}
